// MAIN-845/846/849/879: AI-assisted menu import drawer — source picker
// (shell), the PDF upload step, and the website link step. The "Ready for
// review" done step links straight into MAIN-849's real review page.
//
// The link step creates WebsiteUrl jobs. ezCater pages are refused (here and
// by the API) until access rights are confirmed; existing EzCaterUrl jobs
// still review as before.
import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { useDropzone } from "react-dropzone";
import {
  isEzCaterImportEnabled,
  isMarketplaceUrl,
  isValidWebsiteMenuUrl,
} from "./websiteMenuUrl.utils";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  LinearProgress,
} from "@mui/material";
import { GET_MENU_IMPORT_UPLOAD_URL, LIST_MENU_IMPORT_JOBS } from "../../../Graphql/queries";
import { CREATE_MENU_IMPORT_JOB } from "../../../Graphql/mutations";
import { toggleSnackbar } from "../../../ReactiveVariables/index";
import {
  createMenuImportJobId,
  getImportStartErrorMessage,
  getUploadTargetOrThrow,
} from "./menuImportDrawer.utils";
import { getImportProgress } from "../menuImportProgress.utils";

const MAX_PDF_BYTES = 20 * 1024 * 1024; // 20 MB, matching the mockup's stated limit

// MAIN-845: generic — every source ends up in one of these states.
const STEP = {
  SOURCE: "source",
  PDF: "pdf",
  LINK: "link",
  PROGRESS: "progress",
  DONE: "done",
};

export default function MenuImportDrawer(props) {
  const { open, onClose, catererId, onAddManually } = props;
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.SOURCE);
  const [file, setFile] = useState(null);
  const [defaultDietary, setDefaultDietary] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [jobId, setJobId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // MAIN-845: why the last start failed, kept in the drawer next to the
  // source the caterer is fixing. The snackbar alone disappears after 3s.
  const [startError, setStartError] = useState("");

  const goToStep = (nextStep) => {
    setStartError("");
    setStep(nextStep);
  };

  const resetAndClose = () => {
    setStep(STEP.SOURCE);
    setFile(null);
    setDefaultDietary("");
    setWebsiteUrl("");
    setJobId(null);
    setSubmitting(false);
    setStartError("");
    onClose();
  };

  const [getUploadUrl] = useLazyQuery(GET_MENU_IMPORT_UPLOAD_URL, {
    fetchPolicy: "network-only",
  });

  // No onError here: with one, a failed mutate resolves instead of throwing,
  // and the start functions below lost the server's message. Failures now
  // reach their catch blocks, which show that message exactly once.
  // MAIN-879: refreshing the job list hands the new job to the menu page
  // banner, which is the one place that polls while an import runs.
  const [createMenuImportJob] = useMutation(CREATE_MENU_IMPORT_JOB, {
    refetchQueries: ["ListMenuImportJobs"],
  });

  function showStartError(err) {
    const message = getImportStartErrorMessage(err);
    setSubmitting(false);
    setStartError(message);
    toggleSnackbar({ status: true, message, variant: "error" });
  }

  // MAIN-879: no polling here. The menu page banner already polls the job list
  // while any import is running (and stops when none is); the drawer reads the
  // same cached list, so one request stream drives both, and the job stays
  // trackable after the drawer closes or the page reloads.
  const { data: jobsData } = useQuery(LIST_MENU_IMPORT_JOBS, {
    variables: { catererId },
    skip: !jobId || !catererId,
    fetchPolicy: "cache-only",
  });
  const job = (jobsData?.listMenuImportJobs || []).find((entry) => entry._id === jobId);
  const progress = getImportProgress(job);

  useEffect(() => {
    // NEEDS_REVIEW is the real terminal state of a successful parse — it is
    // NOT "done"; nothing has been reviewed or published yet.
    if (job?.status === "NEEDS_REVIEW") setStep(STEP.DONE);
  }, [job]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    maxSize: MAX_PDF_BYTES,
    onDropRejected: (rejections) => {
      const reason = rejections?.[0]?.errors?.[0]?.message || "File rejected";
      toggleSnackbar({ status: true, message: reason, variant: "error" });
    },
    onDrop: (accepted) => {
      if (accepted?.[0]) {
        setFile(accepted[0]);
        setStartError("");
      }
    },
  });

  async function startPdfImport() {
    if (!file) return;
    setSubmitting(true);
    setStartError("");
    try {
      const target = getUploadTargetOrThrow(
        await getUploadUrl({
          variables: {
            catererId,
            filename: file.name,
          },
        }),
      );
      await axios.put(target.signedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "X-Requested-With",
        },
      });

      const newJobId = await createMenuImportJobId(createMenuImportJob, {
        catererId,
        sourceType: "Pdf",
        sourceFile: {
          filename: target.filename,
          storageKey: target.filename,
          publicUrl: target.publicUrl,
        },
        defaultDietary: defaultDietary || undefined,
      });
      setJobId(newJobId);
      setSubmitting(false);
      setStep(STEP.PROGRESS);
    } catch (err) {
      showStartError(err);
    }
  }

  async function startWebsiteImport() {
    if (!isValidWebsiteMenuUrl(websiteUrl)) return;
    setSubmitting(true);
    setStartError("");
    try {
      const newJobId = await createMenuImportJobId(createMenuImportJob, {
        catererId,
        sourceType: "WebsiteUrl",
        sourceUrl: websiteUrl,
      });
      setJobId(newJobId);
      setSubmitting(false);
      setStep(STEP.PROGRESS);
    } catch (err) {
      showStartError(err);
    }
  }

  return (
    <Drawer
      className="common-drawer"
      anchor="right"
      open={open}
      onClose={resetAndClose}>
      <div className="drawer-head">
        <h4 className="h4">Import Menu</h4>
        <span style={{ cursor: "pointer" }} onClick={resetAndClose}>
          <img src="../assets/images/x-icon.svg" className="img-fluid" alt="close icon" />
        </span>
      </div>

      {/*
        MAIN-846: the .create-step-form wrapper is what makes the drawer footer
        reachable, and is what every other drawer in this app uses. Without it
        the body (.common-drawer .container-fluid has min-height:100%) grows to
        the full drawer height and pushes .createStepBtn below the fold, so the
        Back / Cancel / Start import row sits off-screen and the caterer sees an
        empty drawer with no way to continue. .create-step-form is sized
        height:calc(100vh - 175px) with overflow-y:auto, reserving room for
        .drawer-head plus the footer and scrolling the body instead.
      */}
      <div className="create-step-form">
        <div className="container-fluid py-3">
        {step === STEP.SOURCE && (
          <div className="menu-import-sources">
            <button
              type="button"
              className="menu-import-source-btn"
              onClick={() => goToStep(STEP.PDF)}>
              <b>Upload a menu PDF</b>
              <small>We read the file and turn it into menu items you can edit.</small>
            </button>
            <button
              type="button"
              className="menu-import-source-btn"
              onClick={() => goToStep(STEP.LINK)}>
              <b>Import from Website</b>
              <small>Paste a public menu link for a business you are authorized to manage.</small>
            </button>
            <button
              type="button"
              className="menu-import-source-btn"
              onClick={() => {
                resetAndClose();
                onAddManually && onAddManually();
              }}>
              <b>Add items one at a time</b>
              <small>Type in a dish, set a price, add a photo. Best for a short menu.</small>
            </button>
            <p className="menu-import-note">
              Importing never overwrites what you have. Nothing goes live until you review and
              publish it.
            </p>
          </div>
        )}

        {step === STEP.PDF && (
          <div>
            <div
              {...getRootProps()}
              className={`menu-import-drop${isDragActive ? " active" : ""}`}>
              <input {...getInputProps()} />
              <div>Drop your menu PDF here, or click to browse</div>
              <small>PDF up to 20 MB</small>
            </div>
            {file && (
              <div className="menu-import-file-row">
                <span>{file.name}</span>
                <button
                  type="button"
                  className="menu-import-remove"
                  onClick={() => {
                    setFile(null);
                    setStartError("");
                  }}>
                  Remove
                </button>
              </div>
            )}
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="default-dietary-label">
                If the PDF doesn't say, treat items as
              </InputLabel>
              <Select
                labelId="default-dietary-label"
                label="If the PDF doesn't say, treat items as"
                value={defaultDietary}
                onChange={(e) => setDefaultDietary(e.target.value)}>
                <MenuItem value="">Leave blank for me to fill in</MenuItem>
                <MenuItem value="Non-Vegetarian">Non-vegetarian</MenuItem>
                <MenuItem value="Vegetarian">Vegetarian</MenuItem>
              </Select>
            </FormControl>
            <p className="menu-import-hint">
              Prices, categories and dietary tags come straight from the file. Anything we can't
              read is left blank for you.
            </p>
            {startError && (
              <p className="menu-import-error" role="alert">
                {startError}
              </p>
            )}
          </div>
        )}

        {step === STEP.LINK && (
          <div>
            <TextField
              fullWidth
              label="Menu website link"
              placeholder="https://your-restaurant.com/menu"
              value={websiteUrl}
              onChange={(e) => {
                setWebsiteUrl(e.target.value);
                setStartError("");
              }}
              error={Boolean(websiteUrl) && !isValidWebsiteMenuUrl(websiteUrl)}
              helperText={
                isMarketplaceUrl(websiteUrl) && !isEzCaterImportEnabled()
                  ? "ezCater pages can't be imported as a website. Use your own menu page or a PDF."
                  : isMarketplaceUrl(websiteUrl)
                    ? isValidWebsiteMenuUrl(websiteUrl)
                      ? "ezCater storefront. Check the imported items against your ezCater menu before publishing."
                      : "Use an ezCater storefront link like https://www.ezcater.com/catering/your-restaurant."
                    : Boolean(websiteUrl) && !isValidWebsiteMenuUrl(websiteUrl)
                    ? "Enter a public HTTP or HTTPS menu link."
                    : "Use a direct public HTML menu page. Restricted pages may require a PDF instead."
              }
              margin="normal"
              size="small"
            />
            <p className="menu-import-hint">
              We only pull menus for caterers who ask us to. Nothing goes live until you review
              and publish it.
            </p>
            {startError && (
              <p className="menu-import-error" role="alert">
                {startError}
              </p>
            )}
          </div>
        )}

        {step === STEP.PROGRESS && (
          <div className="menu-import-progress" aria-live="polite">
            <p>
              <b>{progress.headline}</b>
            </p>
            {progress.state === "failed" ? (
              <>
                {progress.category && <p className="menu-import-hint">{progress.category}</p>}
                <p className="menu-import-error" role="alert">
                  {progress.detail}
                </p>
              </>
            ) : (
              <>
                {/* Indeterminate: no step can say how far through it is. */}
                <LinearProgress aria-label="Import in progress" />
                {progress.detail && <p className="menu-import-hint">{progress.detail}</p>}
                <ol className="menu-import-progress-steps">
                  {progress.steps.map((item) => (
                    <li key={item.key} className={item.status}>
                      {item.status === "done" ? "✓ " : item.status === "current" ? "• " : ""}
                      {item.label}
                    </li>
                  ))}
                </ol>
              </>
            )}
            {job?.status === "FAILED" && (
              <Button variant="outlined" onClick={() => goToStep(STEP.SOURCE)}>
                Try again
              </Button>
            )}
          </div>
        )}

        {step === STEP.DONE && (
          <div>
            <p>
              <b>Ready for review.</b> {job?.proposals?.length ?? 0} item
              {job?.proposals?.length === 1 ? "" : "s"} found. Nothing is live yet.
            </p>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                resetAndClose();
                navigate(`/menu-import/${jobId}/review`);
              }}>
              Review items
            </Button>
          </div>
        )}
        </div>
      </div>

      <div className="createStepBtn">
        {(step === STEP.PDF || step === STEP.LINK) && (
          <Button variant="contained" color="secondary" onClick={() => goToStep(STEP.SOURCE)}>
            Back
          </Button>
        )}
        <Button className="cancelBtn" variant="contained" color="secondary" onClick={resetAndClose}>
          {step === STEP.DONE ? "Close" : "Cancel"}
        </Button>
        {step === STEP.PDF && (
          <Button
            variant="contained"
            color="primary"
            disabled={!file || submitting}
            onClick={startPdfImport}>
            {submitting ? "Starting…" : "Start import"}
          </Button>
        )}
        {step === STEP.LINK && (
          <Button
            variant="contained"
            color="primary"
            disabled={!isValidWebsiteMenuUrl(websiteUrl) || submitting}
            onClick={startWebsiteImport}>
            {submitting ? "Starting…" : "Start import"}
          </Button>
        )}
      </div>
    </Drawer>
  );
}
